<template>
  <div style="margin-top: 60px" class="home">
    <Navbar :cartIsVisible="false"></Navbar>

    <div id="success-section" class="success-section">
      <h1 class="section-title" :style="{ color: sectionTitleColorTheme }">
        {{ $t("payment.yourOrderIsInProgress") }}
        <br />
        <br />{{ $t("payment.itWillReachYou") }}
        <span class="tableQR-header"
          >{{ $t("payment.desk") }} {{ tableQRLabel(tableQR) }}</span
        >
      </h1>
    </div>
    <SendEmailModal
      ref="sendEmailModal"
      :isPaid="isPaid"
      :paymentType="paymentType"
      :tableQR="tableQR"
      :paymentOn="paymentOn"
      :paymentMethod="paymentMethod"
    ></SendEmailModal>
  </div>
</template>

<script>
import { getPaymentById, updatePayment } from "@/collections/payments";
import { firestoreTimestamp } from "@/firebaseConfig";
import Navbar from "@/components/public/common/Navbar";
import firebaseMixin from "@/mixins/firebase/firebaseMixin";
import SendEmailModal from "@/components/public/modals/SendEmailModal";
import moment from "moment";
import SuccessMixin from "@/mixins/success/SuccessMixin";
import { getQrZoneById } from "@/collections/qrZone";
import { saveOrder } from "@/collections/orders";
import { getQRByTable } from "@/collections/qrs";
import { deleteAllUserCartByTable } from "@/collections/userCart";

export default {
  name: "SuccessApplePay",
  mixins: [firebaseMixin, SuccessMixin],
  components: {
    Navbar,
    SendEmailModal
  },
  computed: {
    paymentId() {
      return this.$router.currentRoute.query.p_id;
    },
    externalPaymentId() {
      return this.$router.currentRoute.query.externalId;
    },
    homeUrl() {
      return `/?qr=${this.tableQR}`;
    }
  },
  data() {
    return {
      orderCreated: false,
      total: 0,
      isPaid: false, // should pay or request
      paymentType: "product",
      paymentMethod: "applepay",
      canBeDirect: false,
      lastOrder: this.$store.state.cart,
      paymentOn: null
    };
  },
  beforeRouteEnter(to, from, next) {
    if (!to.query.p_id || !to.query.externalId) {
      next("/");
    } else {
      next();
    }
  },
  async mounted() {
    try {
      let payment = await getPaymentById();
      if (payment && payment.success) {
        this.$router.replace(this.homeUrl);
        return;
      }

      // set payment done at
      this.paymentOn = moment().toDate();

      this.canBeDirect = await this.isDirectPayment();

      // is any type of payment done or just requested
      this.isPaid = localStorage.getItem("isPaid") === "true";

      // variable for if user want to pay
      this.paymentType = localStorage.getItem("payment_type"); // "product" or "amount"

      if (this.canBeDirect) {
        this.saveDirectSuccessOrder();
      } else {
        if (!this.isPaid) {
          // save order as requested
          await this.saveSuccessOrder();
        } else {
          // deduct amount from remaining to pay
          await this.updateOrderTotalByTable();

          if (this.paymentType === "product") {
            // set is paid to true in particular order which is selected in ProductChooserDialog
            this.setIsPaid();
          }
        }
      }
    } catch (error) {
      this.setlogError(
        "get_payment_by_id",
        "Obtained error message in mounted()",
        error.stack,
        this.$options.name
      );
    }
  },
  methods: {
    async isDirectPayment() {
      try {
        let table = await getQRByTable(this.tableQR);
        if (table) {
          let zoneQR = await getQrZoneById(table.zone_id);
          return zoneQR ? zoneQR.acceptDirectPayment : false;
        }
        return false;
      } catch (error) {
        // eslint-disable-next-line
        this.setlogError(
          "get/qr_by_table/qr_zone_by_id",
          "Obtained error message in isDirectPayment()",
          error.stack,
          this.$options.name
        );
        return false;
      }
    },
    async saveDirectSuccessOrder() {
      try {
        let self = this;

        if (this.orderCreated === false) {
          let time = firestoreTimestamp.fromDate(new Date());
          let orders = [...this.cart];
          this.cart.forEach(item => {
            let id = self.uuidv4();
            saveOrder(id, self.buildOrder(item, time, id));
            self.total += item.price * item.quantity;
          });

          // set total order quantity in localstorage
          this.saveMyOrders();

          // save general comments in firebase
          this.saveGeneralComment(time);

          this.showSuccessMessage(this.$t("payment.paymentMade"));

          // create order user log
          this.setlogInfo(
            "create_order",
            "Pedido creado",
            [...this.cart],
            this.$options.name
          );

          // Update success: true to prevent using same URL to make other orders
          await updatePayment(this.paymentId, {
            success: true
          });

          this.orderCreated = true;

          // clear everything from local storage
          this.$store.commit("removeAllCart");
          await deleteAllUserCartByTable(this.tableQR, false);
          window.localStorage.setItem("lastLand", "");

          this.$refs.sendEmailModal.openDialog(
            orders,
            self.total,
            this.canBeDirect
          );
        }
      } catch (error) {
        this.setlogError(
          "create_order",
          "Obtained error message in saveDirectSuccessOrder()",
          error.stack,
          this.$options.name
        );
      }
    },
    async saveSuccessOrder() {
      let self = this;
      try {
        if (this.orderCreated === false) {
          let time = firestoreTimestamp.fromDate(new Date());

          this.cart.forEach(item => {
            let id = self.uuidv4();
            saveOrder(id, self.buildOrder(item, time, id));
            self.total += item.isMenuGroupProduct
              ? item.price * item.quantity +
                this.totalWithAdditionalCharge(item)
              : item.price * item.quantity;
          });

          // save amount in orders_total_by_table collection
          await this.addAmountOrderTotalByTable(time);

          // set total order quantity in localstorage
          this.saveMyOrders();

          // save general comments in firebase
          this.saveGeneralComment(time);

          this.showSuccessMessage(this.$t("cart.orderPlaced"));

          // create order user log
          this.setlogInfo(
            "create_order",
            "Pedido creado",
            [...this.cart],
            this.$options.name
          );

          // Update success: true to prevent using same URL to make other orders
          await updatePayment(this.paymentId, {
            success: true
          });

          this.orderCreated = true;

          // clear everything from local storage
          await deleteAllUserCartByTable(this.tableQR, false);
          this.$store.commit("removeAllCart");
          window.localStorage.setItem("lastLand", "");

          this.$router.push("/receipt");
        }
      } catch (error) {
        this.setlogError(
          "update_payment",
          "Obtained error message in saveSuccessOrder()",
          error.stack,
          this.$options.name
        );
      }
    }
  }
};
</script>

<style scoped lang="scss"></style>
